import React, { useState, useEffect, useMemo } from 'react';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { usePlanes } from '@/hooks/usePlanes';
import { usePlanIndicatorStats } from '@/hooks/usePlanIndicatorStats';
import { usePaymentHistory } from '@/hooks/usePaymentHistory';
import { ArrowUpIcon, SparklesIcon, BuildingOfficeIcon, TruckIcon, WrenchScrewdriverIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import toastService from '@/utils/toastService';

/**
 * PlanIndicator simplificado - mantiene todas las funcionalidades
 */
const SimplePlanIndicator = ({ isMobile = false, onModalOpen }) => {
  const { empresaActual } = useSimpleEmpresa();
  const { planes, loading: planesLoading } = usePlanes();
  
  // Usar useMemo para estabilizar las llamadas a los hooks
  const stableEmpresaId = useMemo(() => empresaActual?.id, [empresaActual?.id]);
  const stablePlanId = useMemo(() => empresaActual?.plan_id, [empresaActual?.plan_id]);
  
  // Solo llamar hooks cuando tengamos datos estables
  const shouldLoadStats = Boolean(stableEmpresaId && stablePlanId && planes.length > 0);
  
  const { 
    usageMetrics, 
    overallUsage, 
    criticalMetric, 
    upgradeRecommendation,
    trialInfo,
    loading: statsLoading 
  } = usePlanIndicatorStats();
  
  const { paymentInfo, loading: paymentLoading } = usePaymentHistory();
  
  // Valores estables memoizados
  const empresaId = useMemo(() => empresaActual?.id, [empresaActual?.id]);
  const empresaPlanId = useMemo(() => empresaActual?.plan_id, [empresaActual?.plan_id]);
  const empresaStable = useMemo(() => empresaActual ? {
    id: empresaActual.id,
    plan_id: empresaActual.plan_id,
    nombre: empresaActual.nombre
  } : null, [empresaActual?.id, empresaActual?.plan_id, empresaActual?.nombre]);
  
  // Obtener el plan actual con memoización
  const planActual = useMemo(() => {
    if (!planes.length || !empresaPlanId) return planes[0] || null;
    return planes.find(plan => plan.id === empresaPlanId) || planes[0];
  }, [planes, empresaPlanId]);
  
  // Detectar cambios de plan y mostrar toast - optimizado con referencia estable
  const [prevPlanId, setPrevPlanId] = useState(empresaPlanId);
  const stablePlanesLength = useMemo(() => planes.length, [planes.length]);
  
  useEffect(() => {
    if (prevPlanId && empresaPlanId && prevPlanId !== empresaPlanId && stablePlanesLength > 0) {
      const nuevoPlan = planes.find(plan => plan.id === empresaPlanId);
      if (nuevoPlan) {
        toastService.success(`¡Plan actualizado exitosamente a ${nuevoPlan.name}!`, {
          duration: 6000
        });
      }
    }
    if (empresaPlanId !== prevPlanId) {
      setPrevPlanId(empresaPlanId);
    }
  }, [empresaPlanId, stablePlanesLength, prevPlanId, planes]); // Usar stablePlanesLength
  
  // Calcular información de uso y trial (memoizado)
  const calculatedValues = useMemo(() => {
    const showTrialProgress = !!trialInfo;
    const usagePercentage = showTrialProgress ? trialInfo.porcentajeUsado : (overallUsage || 0);
    
    const shouldShowUpgrade = upgradeRecommendation?.recommended || 
                            trialInfo?.trialExpirado || 
                            trialInfo?.trialProximoAExpirar || 
                            usagePercentage >= 70 || 
                            (planes.findIndex(p => p.id === planActual?.id) < planes.length - 1);
    
    return {
      showTrialProgress,
      usagePercentage, 
      shouldShowUpgrade
    };
  }, [trialInfo, overallUsage, upgradeRecommendation, planes, planActual?.id]);
  
  const { showTrialProgress, usagePercentage, shouldShowUpgrade } = calculatedValues;

  // Manejar upgrade (memoizado)
  const handleUpgradeClick = useMemo(() => () => {
    const currentPlanIndex = planes.findIndex(p => p.id === planActual?.id);
    const nextPlan = planes[currentPlanIndex + 1];
    const targetId = nextPlan ? nextPlan.id : planes[planes.length - 1]?.id;
    
    if (!targetId) {
      console.error('No se pudo encontrar un plan objetivo');
      return;
    }
    
    // Notificar al componente padre para que maneje el modal
    if (onModalOpen) {
      onModalOpen(targetId);
    }
  }, [planes, planActual?.id, onModalOpen]);
  
  if (planesLoading || statsLoading || paymentLoading || !planActual) {
    return (
      <div className={`${isMobile ? 'mb-2' : 'px-4 mb-2'} animate-pulse`}>
        <div className="bg-gray-700 rounded-lg p-3">
          <div className="h-4 bg-gray-600 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-600 rounded w-1/2"></div>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <div className={`${isMobile ? 'mb-3' : 'mb-1'}`}>
        <div className={`${!isMobile ? 'flex items-center px-4 py-2 rounded transition-colors text-white hover:bg-[#262F45]' : ''}`}>
          <div className={`bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-3 border border-gray-700 w-full`}>
          {/* Layout en columnas: texto (60%) y gráfico (40%) */}
          <div className="flex items-start justify-between mb-2">
            {/* Columna de texto */}
            <div className={`flex-1 pr-0 ${!isMobile ? 'w-3/5' : 'min-w-0'}`}>
              <div className="flex items-center space-x-2 mb-0.5">
                <div className={`${isMobile ? 'text-xs' : 'text-sm'} font-bold text-white`} style={isMobile ? {fontSize: '11px'} : {}}>
                  {planActual.name}
                </div>
                {paymentInfo && paymentInfo.hasValidInterval && (
                  <span className={`text-xs ${isMobile ? 'px-1 py-px' : 'px-1.5 py-0.5'} rounded whitespace-nowrap ${
                    paymentInfo.isMonthly ? 'bg-purple-600 text-purple-100' : 
                    paymentInfo.isAnnual ? 'bg-green-600 text-green-100' : 
                    'bg-gray-600 text-gray-100'
                  }`} style={isMobile ? {fontSize: '10px'} : {}}>
                    {paymentInfo.isMonthly 
                      ? 'Mensual' 
                      : paymentInfo.isAnnual 
                      ? `Anual ${planActual?.descuento_anual ? `(-${planActual?.descuento_anual}%)` : ''}`.trim()
                      : 'Plan'}
                  </span>
                )}
                {paymentInfo && !paymentInfo.hasValidInterval && (
                  <span className={`text-xs ${isMobile ? 'px-1 py-px' : 'px-1.5 py-0.5'} rounded bg-yellow-600 text-yellow-100`} style={isMobile ? {fontSize: '10px'} : {}}>
                    Sin período definido
                  </span>
                )}
              </div>
              
              <div className="text-xs text-gray-300" style={isMobile ? {fontSize: '11px'} : {fontSize: '13px'}}>
                {paymentInfo ? (
                  <span>{paymentInfo.formattedAmount}{paymentInfo.periodText}</span>
                ) : planActual?.priceFormatted ? (
                  <span>{planActual.priceFormatted}{planActual.period || ''}</span>
                ) : (
                  <span>Plan {planActual?.name || 'Desconocido'}</span>
                )}
              </div>
              
              {/* Información adicional del pago */}
              {paymentInfo && paymentInfo.dolarObservado && (
                <div className="text-xs text-gray-400 space-y-0" style={isMobile ? {fontSize: '11px'} : {fontSize: '13px'}}>
                  {/* Mostrar precio original del plan en USD */}
                  {planActual && (
                    <div>
                      USD ${paymentInfo.isAnnual 
                        ? (planActual.precio_anual ? Math.round(planActual.precio_anual / paymentInfo.dolarObservado) : Math.round(planActual.price * 12 * 0.8)) 
                        : planActual.price
                      }{paymentInfo.isMonthly ? '/mes' : '/año'}
                    </div>
                  )}
                  <div>Dólar hoy: ${paymentInfo.dolarObservado.toLocaleString('es-CL')}</div>
                </div>
              )}
              
            </div>
            
            {/* Columna del gráfico */}
            <div className={`flex-shrink-0 ${!isMobile ? 'w-2/5' : 'w-auto'}`}>
              <div className={`relative ${isMobile ? 'w-14 h-14 mt-3' : 'w-16 h-16 mt-2'} group cursor-help ml-auto`}>
                  <svg className={`${isMobile ? 'w-14 h-14' : 'w-16 h-16'} transform -rotate-90`} viewBox="0 0 36 36">
                    {/* Fondo del círculo */}
                    <path
                      className="text-gray-600"
                      stroke="currentColor"
                      strokeWidth="3"
                      fill="none"
                      d="M18 2.0845
                         a 15.9155 15.9155 0 0 1 0 31.831
                         a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    {/* Progreso */}
                    <path
                      className={`${
                        showTrialProgress && trialInfo?.trialExpirado ? 'text-red-500' :
                        showTrialProgress && trialInfo?.trialProximoAExpirar ? 'text-orange-500' :
                        showTrialProgress ? 'text-blue-500' :
                        planActual?.id === 'revvly_gold' ? 'text-green-500' :
                        usagePercentage >= 90 ? 'text-red-500' : 
                        usagePercentage >= 70 ? 'text-amber-500' : 
                        'text-purple-500'
                      }`}
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      fill="none"
                      strokeDasharray={`${planActual?.id === 'revvly_gold' ? 100 : usagePercentage}, 100`}
                      d="M18 2.0845
                         a 15.9155 15.9155 0 0 1 0 31.831
                         a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                  
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-xs font-bold text-center leading-tight ${
                      showTrialProgress ? 'text-white' : 
                      planActual?.id === 'revvly_gold' ? 'text-green-400' : 'text-white'
                    }`}>
                      {showTrialProgress ? (
                        <>
                          <div className="text-xs">{trialInfo?.diasRestantes || 0}</div>
                          <div className="text-xs">días</div>
                        </>
                      ) : planActual?.id === 'revvly_gold' ? (
                        <span className="text-xl">∞</span>
                      ) : (
                        `${usagePercentage}%`
                      )}
                    </span>
                  </div>

                {/* Tooltip con detalles de uso */}
                {usageMetrics && (
                  <div className={`absolute bg-gray-900 border border-gray-700 rounded-lg p-3 w-64 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-200 z-50 shadow-xl ${
                    isMobile 
                      ? 'top-full left-1/2 transform -translate-x-1/2 mt-2' 
                      : 'left-full top-0 ml-2'
                  }`}>
                    {/* Flecha del tooltip */}
                    {isMobile ? (
                      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2">
                        <div className="w-2 h-2 bg-gray-900 border-t border-r border-gray-700 transform" style={{transform: 'rotate(-45deg)'}}>
                        </div>
                      </div>
                    ) : (
                      <div className="absolute -left-1 top-4 w-2 h-2 bg-gray-900 border-l border-b border-gray-700 transform rotate-45"></div>
                    )}
                    <div className="text-xs font-medium text-gray-300 mb-2">Detalles de Uso</div>
                    <div className="space-y-2">
                      {Object.entries(usageMetrics).map(([key, metric]) => {
                        const icons = {
                          sucursales: BuildingOfficeIcon,
                          vehiculos: TruckIcon,
                          mantenimientos: WrenchScrewdriverIcon,
                          clientes: UserGroupIcon
                        };
                        const labels = {
                          sucursales: 'Sucursales',
                          vehiculos: 'Vehículos',
                          mantenimientos: 'Mantenimientos',
                          clientes: 'Clientes'
                        };
                        const Icon = icons[key];
                        return (
                          <div key={key} className="flex items-center justify-between text-xs">
                            <div className="flex items-center space-x-2">
                              <Icon className="h-3 w-3 text-gray-400" />
                              <span className="text-gray-300">{labels[key]}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`${
                                metric.isUnlimited ? 'text-green-400' :
                                metric.isAtLimit ? 'text-red-400' : 
                                metric.isNearLimit ? 'text-amber-400' : 
                                'text-gray-300'
                              }`}>
                                {metric.isUnlimited ? `${metric.actual}/∞` : `${metric.actual}/${metric.max}`}
                              </span>
                              <div className="w-16 bg-gray-700 rounded-full h-1.5">
                                <div 
                                  className={`h-1.5 rounded-full ${
                                    metric.isUnlimited ? 'bg-green-500' :
                                    metric.percentage >= 90 ? 'bg-red-500' : 
                                    metric.percentage >= 70 ? 'bg-amber-500' : 
                                    'bg-purple-500'
                                  }`}
                                  style={{ width: `${metric.isUnlimited ? 100 : Math.min(metric.percentage, 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>

            {/* Mensajes y botones de upgrade */}
            {shouldShowUpgrade && (
              <div className={`${isMobile ? 'space-y-1 text-center' : 'space-y-2'}`}>
                {!isMobile && (
                  <div className="text-xs text-gray-300">
                    {showTrialProgress ? (
                      <div className="text-center">
                        {trialInfo?.trialExpirado ? (
                          <span className="text-red-400 font-semibold">¡Trial expirado! Actualiza tu plan</span>
                        ) : trialInfo?.trialProximoAExpirar ? (
                          <span className="text-orange-400">Trial expira en {trialInfo.diasRestantes} días</span>
                        ) : (
                          <span>Trial activo - {trialInfo?.diasRestantes} días restantes</span>
                        )}
                      </div>
                    ) : usagePercentage >= 80 
                      ? `Has usado ${usagePercentage}% de tu capacidad disponible. ¿Necesitas más?`
                      : "Desbloquea más funcionalidades"
                    }
                  </div>
                )}
                
                <button
                  type="button"
                  onClick={handleUpgradeClick}
                  onTouchStart={(e) => {
                    e.preventDefault();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    handleUpgradeClick();
                  }}
                  className={`flex items-center space-x-1 text-xs rounded-full transition-all duration-200 transform hover:scale-105 w-full justify-center px-3 py-0.5 ${
                    trialInfo?.trialExpirado 
                      ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white animate-pulse'
                      : trialInfo?.trialProximoAExpirar
                      ? 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white'
                      : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white'
                  }`}
                  style={{
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation'
                  }}
                >
                  <SparklesIcon className="h-3 w-3" />
                  <span className="text-xs">
                    {trialInfo?.trialExpirado ? 'Actualizar Ahora' : 'Actualizar Plan'}
                  </span>
                  <ArrowUpIcon className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* Botón para mostrar todos los planes si no hay upgrade urgente */}
            {!shouldShowUpgrade && (
              <button
                type="button"
                onClick={handleUpgradeClick}
                onTouchStart={(e) => {
                  e.preventDefault();
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleUpgradeClick();
                }}
                className={`w-full mt-0.5 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 hover:text-white rounded transition-all duration-200 flex items-center justify-center space-x-1`}
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation'
                }}
              >
                <span>Ver todos los planes</span>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

    </>
  );
};

SimplePlanIndicator.displayName = 'SimplePlanIndicator';

export default SimplePlanIndicator;